import React from 'react'
import { useForm, usePage, Link } from '@inertiajs/react'
import { Button } from '@/shared/ui/button'
import { NoAccessNotice } from '@/shared/ui/no-access-notice'
import { useCan } from '@/shared/hooks/useCan'
import { Resources, Actions } from '@/shared/rbac'
import { t } from '@/shared/lib/i18n'
import { useBreadcrumbs } from '@/shared/lib/breadcrumbs-context'
import { accessAdminUserPath, adminUserPath, adminUsersPath } from '@/shared/lib/routes'
import {
  AccessTree,
  useAccessTree,
  type SiteOption,
  type DepartmentOption,
  type UnitOption,
} from '@/features/access-tree'

interface UserSummary {
  id: number
  fullName: string
}

interface Props {
  user: UserSummary
  sites: SiteOption[]
  departments: DepartmentOption[]
  units: UnitOption[]
  locationScopeApplicable?: boolean
  [key: string]: unknown
}

const UserAccessPage = () => {
  const { user, sites, departments, units, locationScopeApplicable } = usePage<Props>().props
  const canSubmit = useCan(Resources.ADMIN_USER, Actions.UPDATE)

  useBreadcrumbs([
    { label: t('nav.administration') },
    { label: t('nav.users'), href: adminUsersPath() },
    { label: user.fullName, href: adminUserPath(user.id) },
    { label: t('admin.users.access.title') },
  ])

  const {
    tree,
    selectedUnitIds,
    toggleUnit,
    toggleDepartment,
    toggleSite,
    toggleAll,
    selectAllState,
    materialized,
    computeDepartmentState,
    computeSiteState,
  } = useAccessTree(sites, departments, units)

  const form = useForm({
    siteIds: materialized.siteIds,
    departmentIds: materialized.departmentIds,
    unitIds: materialized.unitIds,
  })

  React.useEffect(() => {
    form.setData({
      siteIds: materialized.siteIds,
      departmentIds: materialized.departmentIds,
      unitIds: materialized.unitIds,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [materialized])

  const isLocationScopeApplicable = locationScopeApplicable !== false

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">{t('admin.users.access.title')}</h1>
        <p className="text-muted-foreground">{user.fullName}</p>
      </div>

      {!isLocationScopeApplicable && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
          <p className="font-medium text-amber-900">{t('admin.users.access.notApplicable')}</p>
          <p className="mt-1 text-sm text-amber-800">
            {t('admin.users.access.notApplicableDescription')}
          </p>
          <div className="mt-4">
            <Button asChild variant="outline">
              <Link href={adminUserPath(user.id)}>{t('admin.users.access.backToProfile')}</Link>
            </Button>
          </div>
        </div>
      )}

      {isLocationScopeApplicable && (
        <form
          onSubmit={(formEvent) => {
            formEvent.preventDefault()
            form.patch(accessAdminUserPath(user.id))
          }}
          className="space-y-6"
        >
          <AccessTree
            sites={sites}
            departments={departments}
            units={units}
            tree={tree}
            selectedUnitIds={selectedUnitIds}
            toggleUnit={toggleUnit}
            toggleDepartment={toggleDepartment}
            toggleSite={toggleSite}
            toggleAll={toggleAll}
            selectAllState={selectAllState}
            computeDepartmentState={computeDepartmentState}
            computeSiteState={computeSiteState}
            materialized={materialized}
          />

          {!canSubmit && <NoAccessNotice />}

          <div className="flex gap-2">
            {canSubmit && (
              <Button type="submit" disabled={form.processing}>
                {t('admin.users.access.saveButton')}
              </Button>
            )}
            <Button asChild variant="outline">
              <Link href={adminUserPath(user.id)}>{t('admin.users.access.backToProfile')}</Link>
            </Button>
          </div>
        </form>
      )}
    </div>
  )
}

export default UserAccessPage
